/**
 * Account usage metrics - storage/pages/locales/AI for every managed site + the account's
 * site quota. DISPLAY ONLY (`GET /api/account/usage`). Sites are grouped by
 * account (sibling sites share the sites.max quota).
 */
import { useEffect, useState, type ReactNode } from "react";
import { Gauge, Globe, HardDrive, FileText, Image as ImageIcon, Clapperboard, Languages, Sparkles } from "lucide-react";
import { Badge, Card, CardContent, CardHeader, CardTitle, UsageBar, formatBytes, LoadingState } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { usage, type AccountUsage, type SiteUsageRow, type UsageMetric } from "../../lib/usage";

export function UsageSection() {
  const t = useT();
  const [data, setData] = useState<AccountUsage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    usage.account()
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const sites = data?.sites ?? [];
  // Group by account (the quota is account-level; the same for all sibling sites).
  const groups = new Map<string, SiteUsageRow[]>();
  for (const s of sites) {
    const key = s.accountId ?? s.siteId;
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(s);
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Gauge className="size-5 text-primary" /> {t("usage.title")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("usage.subtitle")}</p>
      </div>

      {loading ? (
        <LoadingState label={t("common.loading")} />
      ) : sites.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">{t("usage.empty")}</CardContent></Card>
      ) : (
        [...groups.entries()].map(([groupKey, groupSites]) => {
          const quota = groupSites.find((s) => s.quota)?.quota ?? null;
          return (
            <div key={groupKey} className="space-y-3">
              {quota && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Globe className="size-4" />
                  <span>{t("usage.sitesQuota", {
                    used: quota.used,
                    limit: quota.limit < 0 ? "∞" : quota.limit,
                  })}</span>
                </div>
              )}
              {groupSites.map((site) => <SiteUsageCard key={site.siteId} site={site} t={t} />)}
            </div>
          );
        })
      )}
    </div>
  );
}

function SiteUsageCard({ site, t }: { site: SiteUsageRow; t: (k: string, v?: Record<string, string | number>) => string }) {
  const num = (n: number) => n.toLocaleString();
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{site.name}</CardTitle>
          <Badge variant="muted">{t("usage.planLabel", { plan: t(`usage.plan.${site.planCode}`) || site.planCode })}</Badge>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <Labeled icon={HardDrive} label={t("usage.storage")}>
          <UsageBar
            label={t("usage.storageUsed")}
            used={site.storage.usedBytes}
            limit={site.storage.limit}
            unlimited={site.storage.unlimited}
            ratio={site.storage.ratio}
            warn={site.storage.warn}
            exceeded={site.storage.exceeded}
            format={(n) => formatBytes(n)}
            hint={t("usage.filesCount", { count: site.media.count })}
          />
        </Labeled>
        <Labeled icon={FileText} label={t("usage.pages")}>
          <UsageBar label={t("usage.pagesUsed")} {...metricProps(site.pages)} />
        </Labeled>
        <Labeled icon={Languages} label={t("usage.locales")}>
          <UsageBar label={t("usage.localesUsed")} {...metricProps(site.locales)} />
        </Labeled>
        <Labeled icon={Sparkles} label={t("usage.ai")}>
          <UsageBar
            label={t("usage.aiUsed")}
            used={site.ai.used}
            limit={site.ai.limit}
            unlimited={site.ai.unlimited}
            ratio={site.ai.ratio}
            warn={site.ai.warn}
            exceeded={site.ai.exceeded}
            format={num}
          />
        </Labeled>
        {/* Image/video quota - only when the plan/add-on has an allowance (0/0 = noise). */}
        {site.aiImages && (site.aiImages.limit !== 0 || site.aiImages.used > 0) && (
          <Labeled icon={ImageIcon} label={t("usage.aiImages")}>
            <UsageBar label={t("usage.aiImagesUsed")} {...metricProps(site.aiImages)} />
          </Labeled>
        )}
        {site.aiVideos && (site.aiVideos.limit !== 0 || site.aiVideos.used > 0) && (
          <Labeled icon={Clapperboard} label={t("usage.aiVideos")}>
            <UsageBar label={t("usage.aiVideosUsed")} {...metricProps(site.aiVideos)} />
          </Labeled>
        )}
      </CardContent>
    </Card>
  );
}

function metricProps(m: UsageMetric) {
  return {
    used: m.used, limit: m.limit, unlimited: m.unlimited,
    ratio: m.ratio, warn: m.warn, exceeded: m.exceeded,
    format: (n: number) => n.toLocaleString(),
  };
}

function Labeled({ icon: Icon, label, children }: {
  icon: typeof HardDrive;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="size-3.5" /> {label}
      </div>
      {children}
    </div>
  );
}
